// French legal content served at /mentions-legales, /privacy, /terms and
// /objets-non-pris-en-charge.
// Single source of truth (FR only — the authoritative version for a French company).
// Placeholders from the source drafts are resolved here with standard values; update
// the figures/addresses below if the company details change.

export const LEGAL_LAST_UPDATED = 'Dernière mise à jour : 6 octobre 2026';

// Règles communes (décision David 06/10/2026, voir backend/marketplaces/common_rules.py) :
// ce que Liberio ne prend pas en charge, quel que soit l'acteur. Ouverte par le lien
// « nos conditions » de l'écran « Objet non pris en charge », citée à l'article 10 des CGU.
// Organisation reprise de la page de Sellpy « Que ne vendons-nous pas sur Sellpy ? ».
export const OBJETS_NON_PRIS_EN_CHARGE = {
  title: 'Objets non pris en charge',
  body: `Liberio aide à donner une seconde vie aux objets du quotidien. Les objets ci-dessous ne sont pas pris en charge, pour des raisons d'hygiène, de sécurité ou de réglementation.

## Jamais pris en charge

- Nourriture et boissons
- Médicaments, compléments alimentaires et produits de santé
- Tabac, produits de vapotage et alcool
- Stupéfiants
- Animaux vivants et produits issus d'espèces protégées
- Armes, munitions et répliques d'armes
- Produits dangereux (peintures, solvants, produits chimiques, bouteilles de gaz, feux d'artifice)
- Documents d'identité et moyens de paiement

## Seulement neufs

- Cosmétiques (maquillage, soins, parfums, produits capillaires) : neufs et scellés
- Articles d'hygiène (brosse à dents, rasoir à lames…)

## Seulement en borne textile

- Bas de sous-vêtements portés (culottes, slips, caleçons), pour être recyclés

## Pas encore pris en charge

- Matériaux de construction (bois, carrelage, portes, fenêtres…)`,
};

export const MENTIONS_LEGALES = {
  title: 'Mentions légales',
  body: `## 1. Éditeur du site et de l'application

Le site **liberio.app** et l'application **Liberio** (ci-après le « Service ») sont édités par :

- **Dénomination sociale :** Liberio Studio
- **Forme juridique :** Société par actions simplifiée à associé unique (SASU)
- **Capital social :** 2 000 €
- **Siège social :** 61 rue de Lyon, 75012 Paris (société domiciliée auprès de LES TRICOLORES, SIREN 849 409 313)
- **Immatriculation :** RCS de Paris sous le numéro 106 192 768
- **SIRET (siège) :** 10619276800013
- **Code APE/NAF :** 5829C (Édition de logiciels applicatifs)
- **N° de TVA intracommunautaire :** FR52106192768
- **Président :** la société LBR Gestion, SASU au capital de 500 €, dont le siège social est situé 3 rue de la Louvière, 88000 Épinal, immatriculée au RCS d'Épinal sous le numéro 101 377 448, représentée par M. David Labourier
- **Adresse e-mail de contact :** contact@liberio.app

## 2. Directeur de la publication

Le directeur de la publication est **M. David Labourier**, en qualité de représentant de la société LBR Gestion, présidente de Liberio Studio.

## 3. Hébergement

Le Service est hébergé par les prestataires suivants :

- **Vercel Inc.** (hébergement de l'application web) — 440 N Barranca Ave #4133, Covina, CA 91723, États-Unis.
- **Railway Corporation** (hébergement du back-end et des bases de données) — société de droit américain, San Francisco, Californie, États-Unis.
- **Cloudflare, Inc.** (réseau de diffusion, sécurité) — 101 Townsend Street, San Francisco, CA 94107, États-Unis.

## 4. Contact

Pour toute question relative au Service, vous pouvez écrire à : **contact@liberio.app**.

Pour toute question relative à vos données personnelles, voir la [Politique de confidentialité](/privacy) et l'adresse dédiée : **privacy@liberio.app**.

## 5. Propriété intellectuelle

L'ensemble des éléments du Service (marque « Liberio », logo, textes, interface, code, bases de données, charte graphique) est protégé par le droit de la propriété intellectuelle et demeure la propriété exclusive de Liberio Studio ou de ses partenaires. Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans autorisation écrite préalable, est interdite.

Les contenus déposés par les utilisateurs (notamment les photos) restent leur propriété ; l'utilisateur concède à Liberio Studio une licence limitée d'utilisation de ces contenus, strictement nécessaire au fonctionnement du Service, dans les conditions prévues par les [Conditions Générales d'Utilisation](/terms).

## 6. Sources de données ouvertes

Le Service réutilise le jeu de données public **« Que faire de mes objets & déchets »**, produit et agrégé par l'ADEME, pour recenser les lieux de réemploi, de réparation, de don et de recyclage en France. Ce jeu est disponible sur https://quefairedemesdechets.ademe.fr/.

Conformément aux conditions de réutilisation de ces données :

- la paternité complète de chaque lieu, telle que fournie par l'ADEME, est affichée sur la fiche du lieu concerné ;
- la date de notre dernière récupération des données est indiquée sur cette même fiche ;
- les données et leur sens ne sont ni modifiés ni altérés par le Service.

Une erreur ou une demande de suppression concernant un lieu peut être signalée directement à l'ADEME via le formulaire prévu à cet effet, accessible depuis le site ci-dessus.

Le Service complète ce recensement avec des informations issues du répertoire public **SIRENE** des entreprises, mis à disposition par l'Insee.

## 7. Données personnelles

Le traitement des données personnelles est décrit dans la [Politique de confidentialité](/privacy), accessible à tout moment sur le Service. Conformément au Règlement (UE) 2016/679 (RGPD) et à la loi « Informatique et Libertés », vous disposez de droits sur vos données, que vous pouvez exercer à l'adresse **privacy@liberio.app**.

## 8. Médiation de la consommation

Conformément aux articles L.611-1 et suivants du Code de la consommation, après nous avoir sollicités (contact@liberio.app) et à défaut de réponse vous satisfaisant, le consommateur peut recourir gratuitement au médiateur de la consommation dont relève Liberio Studio :

**CM2C** — 49 rue de Ponthieu, 75008 Paris — Tél. : 01 89 47 00 14 — https://www.cm2c.net/declarer-un-litige.php — litiges@cm2c.net`,
};

export const PRIVACY = {
  title: 'Politique de confidentialité',
  body: `Liberio Studio attache une grande importance à la protection de vos données personnelles. La présente politique explique quelles données nous traitons, pourquoi, sur quelles bases, avec qui nous les partageons, combien de temps nous les conservons et quels sont vos droits.

## 1. Responsable du traitement

Le responsable du traitement est **Liberio Studio**, SASU au capital de 2 000 €, dont le siège social est situé 61 rue de Lyon, 75012 Paris, immatriculée au RCS de Paris sous le numéro 106 192 768.

- **Contact « données personnelles » :** privacy@liberio.app
- **Délégué à la protection des données (DPO) :** aucun DPO n'a été désigné, la désignation n'étant pas obligatoire au regard de l'activité. Vos demandes peuvent être adressées à l'adresse ci-dessus.

## 2. Données que nous collectons

Selon votre utilisation du Service, nous pouvons traiter :

- **Données de compte :** adresse e-mail, mot de passe (sous forme chiffrée), et le cas échéant prénom/nom ou pseudonyme.
- **Photos d'objets :** les photos que vous prenez ou importez pour faire identifier et estimer vos objets.
- **Données relatives aux objets :** catégorie, état, estimation de valeur, plan d'action, options retenues (vente, don, recyclage, réparation, prêt).
- **Données de localisation :** si vous l'autorisez, pour vous proposer des points de collecte et des acteurs locaux pertinents.
- **Données d'usage et techniques :** pages consultées, actions réalisées, type d'appareil, données de connexion, journaux d'erreurs, identifiants techniques et cookies (voir section 9).
- **Communications :** échanges avec le support et e-mails que nous vous envoyons.
- **Achats :** si vous achetez une Offre payante dans l'application, l'historique de vos achats transmis par Apple ou Google (offre, date, état de l'abonnement). Nous n'avons jamais accès à vos données bancaires.
- **Partenaires de Liberio :** quand un acteur (boutique, enseigne, service en ligne) vérifie sa fiche, le nom, le rôle et l'adresse e-mail de la personne qui la valide.

## 3. Finalités et bases légales

- **Créer et gérer votre compte, fournir le Service** — base légale : exécution du contrat (art. 6.1.b RGPD).
- **Identifier et estimer vos objets via l'IA** — exécution du contrat.
- **Gérer vos achats et votre accès aux Offres payantes** — exécution du contrat.
- **Proposer des acteurs et points de collecte locaux** — exécution du contrat / consentement (géolocalisation).
- **Améliorer le Service, mesurer l'audience et la performance de nos campagnes, corriger les erreurs** — intérêt légitime (voir section 9).
- **Vous envoyer des e-mails de service ou, le cas échéant, marketing** — exécution du contrat / consentement.
- **Assurer la sécurité et prévenir les fraudes** — intérêt légitime.
- **Relire et publier la fiche d'un partenaire, et le contacter à son sujet** — intérêt légitime.
- **Respecter nos obligations légales (comptables, fiscales)** — obligation légale.

## 4. Le cas particulier des photos et de l'intelligence artificielle

Pour identifier vos objets et en estimer la valeur, vos photos sont transmises à notre prestataire d'intelligence artificielle **Google** (API Gemini), dont les serveurs sont situés **aux États-Unis**. Cela implique un **transfert de vos données en dehors de l'Union européenne** (voir section 7). Ce traitement est nécessaire à la fourniture du Service. Les données transmises via l'API Gemini ne sont pas utilisées pour entraîner les modèles de Google ; elles peuvent être conservées à des fins de contrôle anti-abus pendant une durée maximale de **55 jours**, puis sont supprimées. Si ce service est indisponible, l'analyse peut être assurée par **OpenAI** (États-Unis), aux mêmes conditions : données non utilisées pour l'entraînement, conservation anti-abus limitée à **30 jours**.

Par ailleurs, les métadonnées techniques embarquées dans vos photos (données EXIF, dont la géolocalisation de la prise de vue) sont **supprimées lors du téléversement** : seuls les pixels de l'image sont conservés.

## 5. Durées de conservation

- **Données de compte :** pendant toute la durée de votre compte. Vous pouvez supprimer votre compte à tout moment depuis les réglages de l'application : vos données personnelles (compte, objets, scans, photos, préférences) sont alors effacées immédiatement. Pour toute autre fermeture, les données sont supprimées ou anonymisées dans un délai de 12 mois.
- **Photos d'un scan sans suite :** si vous n'enregistrez pas l'objet dans votre plan d'action après un scan, ses photos sont supprimées au bout de **30 jours**.
- **Photos des objets de votre plan d'action :** conservées tant que votre compte est utilisé. Si vous supprimez un objet, ses photos sont supprimées immédiatement.
- **Compte inactif :** si votre compte reste inactif pendant **24 mois**, les photos de vos objets sont supprimées ; nous vous prévenons par e-mail **30 jours avant**, et une simple connexion suffit à les conserver.
- **Photos de preuve de dépôt :** supprimées **30 jours** après leur vérification.
- **Données d'estimation :** dans tous les cas ci-dessus, les données décrivant l'objet (catégorie, état, estimation de valeur) sont conservées **sans la photo**, à des fins statistiques et d'amélioration des estimations.
- **Données d'usage / cookies :** 13 mois maximum pour les cookies.
- **Coordonnées de la personne qui valide une fiche partenaire :** tant que la fiche est en ligne, puis 3 ans après le dernier échange.

## 6. Destinataires et sous-traitants

Vos données ne sont jamais vendues. Elles peuvent être partagées avec des prestataires (sous-traitants au sens du RGPD) agissant pour notre compte et selon nos instructions :

- **Google** — identification et estimation des objets (IA, API Gemini) (États-Unis).
- **OpenAI** — identification et estimation des objets, en secours (États-Unis).
- **Vercel** — hébergement de l'application web (États-Unis).
- **Railway** — hébergement du back-end et des bases de données (États-Unis).
- **Cloudflare** — réseau de diffusion, sécurité (États-Unis).
- **Sentry** — suivi et correction des erreurs techniques (États-Unis).
- **PostHog** — mesure d'audience et analyse produit (Union européenne).
- **Resend** — envoi d'e-mails transactionnels (États-Unis).
- **Brevo** — envoi d'e-mails d'accompagnement et d'information (France / Union européenne).
- **RevenueCat** — gestion des achats intégrés et des abonnements (États-Unis). Il reçoit un identifiant de compte technique et l'historique d'achats, jamais votre nom ni vos coordonnées bancaires.

Les paiements des Offres payantes sont traités par **Apple** (App Store) ou **Google** (Google Play), en tant que responsables de traitement distincts, selon leurs propres politiques de confidentialité.

Vos données peuvent également être communiquées aux autorités compétentes lorsque la loi l'exige.

## 7. Transferts en dehors de l'Union européenne

Certains de nos prestataires sont établis ou hébergent des données aux États-Unis (notamment OpenAI, Vercel, Cloudflare, Sentry, Resend et RevenueCat). Ces transferts sont encadrés par des garanties appropriées au sens du RGPD : **clauses contractuelles types** de la Commission européenne et/ou adhésion au **Data Privacy Framework** lorsque le prestataire y est certifié.

## 8. Vos droits

Conformément au RGPD et à la loi « Informatique et Libertés », vous disposez des droits suivants : **accès**, **rectification**, **effacement**, **opposition**, **limitation**, **portabilité**, et **retrait de votre consentement** à tout moment lorsque le traitement repose sur celui-ci.

Pour exercer votre droit à l'effacement, le plus simple est de supprimer votre compte directement depuis l'application (Réglages, puis « Supprimer mon compte ») : l'effacement est immédiat. Pour tous les droits, vous pouvez aussi écrire à **privacy@liberio.app**. Nous pourrons vous demander un justificatif d'identité. Nous répondons dans un délai d'un mois.

Vous pouvez également introduire une réclamation auprès de la **CNIL** (Commission nationale de l'informatique et des libertés), 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07 — www.cnil.fr.

## 9. Cookies et traceurs

Le Service utilise trois familles de traceurs :

- des **traceurs strictement nécessaires** au fonctionnement du Service (authentification, préférences, sécurité) ;
- des **traceurs de mesure d'audience et d'analyse produit** (PostHog, hébergé dans l'Union européenne), utilisés pour comprendre l'usage du Service et corriger les erreurs ;
- des **traceurs de mesure de la performance de nos campagnes** (Google Analytics, pixel Meta), utilisés pour évaluer l'efficacité de nos actions d'acquisition.

Vous pouvez à tout moment bloquer ou supprimer les cookies via les paramètres de votre navigateur ou des extensions dédiées ; le Service reste utilisable. La durée de vie des cookies n'excède pas 13 mois.

## 10. Sécurité

Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données (chiffrement des mots de passe, accès restreints, recours à des prestataires reconnus). Aucun système n'étant infaillible, nous ne pouvons toutefois garantir une sécurité absolue.

## 11. Modifications

La présente politique peut être mise à jour. En cas de modification substantielle, nous vous en informerons par un moyen approprié. La version applicable est celle publiée sur le Service à la date de votre utilisation.`,
};

export const DATA_DELETION = {
  title: 'Supprimer vos données Liberio',
  body: `Trois possibilités, selon ce que vous souhaitez effacer.

## Supprimer un objet

Dans l'application, ouvrez l'objet puis choisissez « Supprimer ». La fiche et ses photos sont effacées immédiatement. Votre compte et vos autres objets ne sont pas affectés.

## Supprimer votre compte et tout ce qu'il contient

Dans l'application, allez dans Réglages puis « Supprimer mon compte ». Sont effacés immédiatement : votre compte, vos objets, vos scans, vos photos, vos préférences et votre historique d'achats chez notre prestataire.

Si vous avez un abonnement Premium acheté sur l'App Store ou Google Play, sa résiliation se fait dans les réglages de votre store : supprimer votre compte ne l'arrête pas.

## Nous écrire

Si vous ne pouvez plus accéder à l'application, écrivez à **privacy@liberio.app**. Nous pouvons vous demander un justificatif d'identité et nous répondons dans un délai d'un mois.

## Ce qui est effacé, et ce qui est conservé

- **Compte, objets, scans, photos, préférences :** effacés immédiatement.
- **Description des objets sans leur photo** (catégorie, état, estimation) : conservée à des fins statistiques et d'amélioration des estimations.

## Suppressions automatiques, sans démarche de votre part

- Photos d'un scan que vous n'enregistrez pas : supprimées après **30 jours**.
- Photos de preuve de dépôt : supprimées **30 jours** après leur vérification.
- Compte inactif depuis **24 mois** : les photos de vos objets sont supprimées, après un e-mail d'avertissement envoyé 30 jours avant.

Pour le détail de nos traitements, consultez la [politique de confidentialité](/privacy).`,
};

// Version « monétisation » (18/09/2026) : Premium et le Pack déménagement se
// vendent uniquement en achat intégré (App Store, Google Play). Aucun prix n'est
// écrit ici : le prix applicable est celui affiché par le store au moment de
// l'achat (leçon du rejet Apple 3.1.1 de septembre, où la CGU décrivait des prix
// qu'on ne vendait pas). Rien sur Liberio Pro, vendu hors de l'app à des
// professionnels.
export const TERMS = {
  title: "Conditions Générales d'Utilisation et de Vente (CGU/CGV)",
  body: `## 1. Objet

Les présentes Conditions Générales d'Utilisation et de Vente (les « CGU/CGV ») régissent l'accès et l'utilisation de l'application **Liberio** et du site **liberio.app** (le « Service »), ainsi que l'achat des Offres payantes proposées dans l'application. Toute utilisation du Service implique l'acceptation pleine et entière des présentes.

## 2. Identification du prestataire

Le Service est édité et exploité par **Liberio Studio**, SASU au capital de 2 000 €, siège social 61 rue de Lyon, 75012 Paris, RCS de Paris 106 192 768 — ci-après « Liberio ». Contact : contact@liberio.app.

## 3. Définitions

- **Utilisateur :** toute personne physique utilisant le Service.
- **Consommateur :** tout Utilisateur agissant à des fins n'entrant pas dans le cadre de son activité professionnelle.
- **Compte :** l'espace personnel créé par l'Utilisateur.
- **Service :** les fonctionnalités proposées par Liberio (identification d'objets par photo, estimation de valeur, recommandations et plan d'action).
- **Offre payante :** l'abonnement Premium ou le Pack déménagement, décrits à l'article 6.

## 4. Description du Service

Liberio est un **outil d'aide à la décision et d'orientation** : à partir d'une photo, le Service identifie un objet, en propose une estimation de valeur d'occasion et suggère des options de « seconde vie » (vendre, donner, recycler, réparer, prêter), en orientant l'Utilisateur vers des plateformes et acteurs existants.

L'Utilisateur reconnaît que :

- Liberio **n'est pas une place de marché** : Liberio ne vend pas, n'achète pas et n'assure pas la transaction des objets.
- Les **estimations de valeur sont indicatives**, fournies à titre informatif, et ne constituent ni une expertise ni une garantie de prix de revente.
- Les informations sur les acteurs locaux (horaires, conditions d'acceptation, etc.) sont fournies à titre indicatif et peuvent évoluer indépendamment de Liberio.

## 5. Accès, compte et éligibilité

L'utilisation du Service nécessite la création d'un Compte avec une adresse e-mail valide. L'Utilisateur s'engage à fournir des informations exactes et à préserver la confidentialité de ses identifiants.

Le Service est réservé aux personnes disposant de la **capacité juridique** de contracter.

## 6. Offres

Le Service est accessible gratuitement. Des Offres payantes, facultatives, en élargissent l'usage.

- **Offre gratuite :** son usage peut être soumis à des limites, notamment un nombre de scans par semaine et un nombre d'objets publiés dans la boutique. Les limites en vigueur sont indiquées dans l'application.
- **Premium :** abonnement donnant accès à des scans illimités et à une boutique sans limite d'objets, proposé en formule **mensuelle** ou **annuelle**.
- **Pack déménagement :** achat unique donnant accès aux mêmes avantages que Premium pendant **six (6) mois** à compter de l'achat. Il ne se renouvelle pas.

Les avantages de chaque Offre payante sont décrits dans l'application au moment de l'achat.

## 7. Achat, prix et paiement

Les Offres payantes s'achètent **dans l'application**, par le système d'achat intégré de l'**App Store** (Apple) ou de **Google Play** (Google), selon votre appareil. Le paiement, la facturation et l'envoi du reçu sont assurés par Apple ou Google, selon leurs propres conditions. Liberio n'a jamais accès à vos données bancaires.

Le prix applicable est celui **affiché dans l'application et par l'App Store ou Google Play au moment de l'achat**, toutes taxes comprises. Une modification de prix ne s'applique pas à une période déjà payée.

L'achat est rattaché au Compte Liberio utilisé au moment de l'achat. En cas de changement d'appareil ou de réinstallation, la fonction « Restaurer mes achats », dans les réglages de l'application, permet de les retrouver.

## 8. Durée, renouvellement et résiliation

- **Premium** est un abonnement à **renouvellement automatique**. Il est reconduit pour une durée identique (un mois ou un an) à la fin de chaque période, au prix alors en vigueur, sauf résiliation au moins **24 heures avant la fin de la période en cours**. Le renouvellement est prélevé par Apple ou Google dans les 24 heures qui précèdent la fin de la période.
- **Résiliation :** vous pouvez résilier à tout moment, sans frais, depuis les **réglages d'abonnement de votre compte Apple ou Google**. L'application vous y conduit depuis ses réglages. La résiliation prend effet à la fin de la période en cours, et vous gardez l'accès jusqu'à cette date.
- **Changement de formule :** le passage de la formule mensuelle à la formule annuelle, ou l'inverse, prend effet à la fin de la période en cours.
- **Pack déménagement :** achat unique, sans renouvellement ni résiliation. L'accès prend fin automatiquement six mois après l'achat.
- **Suppression du Compte :** supprimer votre Compte Liberio **ne résilie pas** un abonnement souscrit via l'App Store ou Google Play. Pensez à le résilier depuis votre store.

## 9. Droit de rétractation et remboursement

Conformément aux articles L.221-18 et suivants du Code de la consommation, le Consommateur dispose d'un délai de **quatorze (14) jours** à compter de l'achat pour exercer son droit de rétractation, sans avoir à se justifier.

Les Offres payantes sont un **service numérique fourni dès l'achat**. En demandant l'accès immédiat au service, le Consommateur accepte qu'il commence avant la fin du délai de rétractation. S'il se rétracte ensuite, un montant proportionnel au service déjà fourni peut rester dû, dans les conditions prévues par la loi.

Les achats étant encaissés par Apple ou Google, les demandes de remboursement s'effectuent auprès d'eux : pour l'App Store, sur **reportaproblem.apple.com** ; pour Google Play, depuis votre compte Google Play. Vous pouvez aussi nous écrire à **contact@liberio.app**, nous vous accompagnerons dans la démarche.

## 10. Obligations de l'Utilisateur

L'Utilisateur s'engage à utiliser le Service conformément à la loi et aux présentes, à ne pas porter atteinte à son fonctionnement ou à sa sécurité, et à ne téléverser que des contenus dont il dispose des droits. Il garantit que les photos importées ne portent pas atteinte aux droits de tiers ni à l'ordre public.

En particulier, le Service est destiné aux objets du quotidien. Ne peuvent être analysés ou publiés via le Service, notamment : les photos dont une personne est le sujet ; les animaux vivants et les produits issus d'espèces protégées ; les armes, munitions et répliques d'armes ; les médicaments et produits de santé ; les stupéfiants ; le tabac, les produits de vapotage et l'alcool ; les contenus à caractère sexuel ; les documents d'identité et moyens de paiement. Liberio peut refuser l'analyse d'une photo, retirer un contenu signalé ou suspendre un compte en cas de manquement.

La liste complète des objets non pris en charge est consultable sur la page [Objets non pris en charge](/objets-non-pris-en-charge).

## 11. Propriété intellectuelle

Le Service et l'ensemble de ses éléments sont protégés et demeurent la propriété de Liberio. L'Utilisateur conserve la propriété de ses contenus (photos notamment) et concède à Liberio une licence limitée, strictement nécessaire au fonctionnement du Service (traitement, identification, estimation), pour la durée d'utilisation.

## 12. Responsabilité

Liberio fournit un service d'aide à la décision et d'orientation. Sa responsabilité ne saurait être engagée :

- au titre du **caractère indicatif des estimations** et des recommandations ;
- au titre des **transactions, services ou prestations réalisés par des tiers** (plateformes, acteurs locaux, repreneurs) vers lesquels l'Utilisateur est orienté ;
- en cas d'**indisponibilité temporaire** du Service ou de **force majeure**.

Les présentes ne limitent pas les droits légaux du Consommateur, notamment les garanties légales applicables.

## 13. Données personnelles

Le traitement des données personnelles est décrit dans la [Politique de confidentialité](/privacy), partie intégrante des présentes. L'Utilisateur peut exercer ses droits à l'adresse **privacy@liberio.app**.

## 14. Médiation de la consommation

Conformément aux dispositions du Code de la consommation concernant la médiation des litiges de la consommation, après nous avoir sollicités (service client : **contact@liberio.app**) et à défaut de réponse vous satisfaisant, vous avez la possibilité de recourir gratuitement à une procédure de médiation de la consommation auprès de **CM2C** (Centre de la Médiation de la Consommation de Conciliateurs de Justice) — 49 rue de Ponthieu, 75008 Paris — Tél. : 01 89 47 00 14 — https://www.cm2c.net/declarer-un-litige.php — litiges@cm2c.net.

La saisine du médiateur suppose une réclamation écrite préalable auprès de Liberio restée sans réponse satisfaisante, et que le litige n'ait pas déjà été examiné par un autre médiateur ou par un tribunal.

## 15. Droit applicable et litiges

Les présentes sont régies par le **droit français**. En cas de litige, et après tentative de résolution amiable, les tribunaux français sont compétents, dans le respect des règles protectrices du Consommateur.

## 16. Modification des CGU/CGV

Liberio peut modifier les présentes. La version applicable est celle en vigueur à la date de l'utilisation. En cas de modification substantielle, l'Utilisateur en est informé.`,
};
